import { routeGroupLabelOf } from "components/SmartSurveyor/shared/routeGroupLabelOf";
import type {
  PickSelectedGroupArgs,
  SelectedGroupAfterHistory,
  SelectedGroupAfterHistoryOf,
} from "./types";

const KEEP: SelectedGroupAfterHistory = { kind: "keep" };
const SELECT_NEXT: SelectedGroupAfterHistory = { kind: "selectNext" };

// A renamed group is followed to its new name; a merged one to the group its items moved into.
const pickSelectedGroup = ({
  selectedGroupLabel,
  typeLabelChanges,
  itemMoves,
  labelOfTypeId,
}: PickSelectedGroupArgs): SelectedGroupAfterHistory => {
  if (selectedGroupLabel === null) return KEEP;
  const selectedType = typeLabelChanges.find(
    (change) => change.before === selectedGroupLabel,
  );
  if (selectedType === undefined || selectedType.after === selectedGroupLabel) {
    return KEEP;
  }
  if (selectedType.after !== null) {
    return { kind: "select", groupLabel: selectedType.after };
  }
  const move = itemMoves.find(
    (itemMove) => itemMove.fromTypeId === selectedType.typeId,
  );
  const targetLabel =
    move === undefined ? undefined : labelOfTypeId(move.toTypeId);
  return targetLabel === undefined
    ? SELECT_NEXT
    : { kind: "select", groupLabel: targetLabel };
};

export const selectedElementGroupAfterHistory: SelectedGroupAfterHistoryOf = ({
  selectedGroupLabel,
  changes,
  labelOfTypeId,
}) =>
  pickSelectedGroup({
    selectedGroupLabel,
    labelOfTypeId,
    typeLabelChanges: changes.flatMap((change) =>
      change.table === "elementTypes"
        ? [
            {
              typeId: change.rowId,
              before: change.before?.name ?? null,
              after: change.after?.name ?? null,
            },
          ]
        : [],
    ),
    itemMoves: changes.flatMap((change) =>
      change.table === "elementMarkers" &&
      change.before !== null &&
      change.after !== null
        ? [
            {
              fromTypeId: change.before.elementTypeId,
              toTypeId: change.after.elementTypeId,
            },
          ]
        : [],
    ),
  });

export const selectedRouteGroupAfterHistory: SelectedGroupAfterHistoryOf = ({
  selectedGroupLabel,
  changes,
  labelOfTypeId,
}) =>
  pickSelectedGroup({
    selectedGroupLabel,
    labelOfTypeId,
    typeLabelChanges: changes.flatMap((change) =>
      change.table === "routeTypes"
        ? [
            {
              typeId: change.rowId,
              before:
                change.before === null
                  ? null
                  : routeGroupLabelOf(change.before.cableType),
              after:
                change.after === null
                  ? null
                  : routeGroupLabelOf(change.after.cableType),
            },
          ]
        : [],
    ),
    itemMoves: changes.flatMap((change) =>
      change.table === "routes" &&
      change.before !== null &&
      change.after !== null
        ? [
            {
              fromTypeId: change.before.routeTypeId,
              toTypeId: change.after.routeTypeId,
            },
          ]
        : [],
    ),
  });
